import { Badge, Button } from '@auctisync/ui';
import {
  SignedOutError,
  fetchCatalogingAuctions,
  fetchCatalogingLots,
  fetchEdgeFunction,
} from '@auctisync/api-client';
import { thumbUrl, type Tables } from '@auctisync/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { compressImage } from '../../lib/compressImage';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import { capturePhotoNative, isNativeCameraAvailable } from '../../lib/nativeCamera';
import {
  enqueuePhoto,
  flushQueue,
  installAutoFlush,
  subscribeQueueSize,
} from '../../lib/offlinePhotoQueue';

/**
 * /admin/catalog — mobile-first field-cataloging page.
 *
 * The field team opens this on their phone, picks an auction, then swipes
 * between pre-created empty lots shooting photos. Photos upload to R2 via
 * upload-photo-to-r2 with the lot's id so the Edge Function can compute
 * the display_filename ({prefix}-{lot:04d}-{seq:02d}) and stamp the lot's
 * cataloged_at for the office team's "needs research" queue.
 *
 * UI priorities: big tap targets, fast camera roundtrip, clear display of
 * which lot you're on + how many photos so far. No keyboard input required.
 */

type AuctionRef = {
  id: string;
  title: string;
  slug: string;
  photo_file_prefix: string | null;
  tenant_id: string;
};

type LotRef = Pick<
  Tables<'lots'>,
  'id' | 'lot_number' | 'control_number' | 'title' | 'cataloged_at'
>;

type PhotoRef = {
  id: string;
  display_url: string;
  image_sequence: number | null;
  display_filename: string | null;
};

export function AdminCatalog() {
  const { session, userId } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const auctionIdParam = searchParams.get('auction');

  const [auctions, setAuctions] = useState<AuctionRef[]>([]);
  const [auction, setAuction] = useState<AuctionRef | null>(null);
  const [lots, setLots] = useState<LotRef[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [photos, setPhotos] = useState<PhotoRef[]>([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Load the list of auctions the user can see (any status, since staff
  // may catalog drafts / queued / preview auctions that aren't live yet).
  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    void (async () => {
      /*
       * 🔴 THE SHARED RULE, not an inline query (2026-09-22). "Which auctions
       * may be catalogued" decides what a warehouse worker is allowed to
       * photograph — and a CLOSED sale is deliberately not on the list, because
       * stamping `cataloged_at` on one edits the record of something people bid
       * real money on. The native Cataloging app asks the same question, so the
       * answer moved to `@auctisync/api-client` rather than being written twice.
       */
      try {
        const rows = await fetchCatalogingAuctions(supabase);
        if (cancelled) return;
        setAuctions(rows as AuctionRef[]);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Set the selected auction once auctions load, honoring ?auction=<id>.
  useEffect(() => {
    if (auctions.length === 0) return;
    if (auctionIdParam) {
      const match = auctions.find((a) => a.id === auctionIdParam);
      if (match) {
        setAuction(match);
        return;
      }
    }
    // No query-param or no match — leave the picker visible.
  }, [auctions, auctionIdParam]);

  // Load lots for the selected auction.
  const refreshLots = useCallback(async () => {
    if (!auction) return;
    setLoadingLots(true);
    /*
     * The shared rule — see the note on the auctions load above. Deliberately
     * NOT `fetchAuctionLots`, which is the PUBLIC read and filters
     * `is_visible`: during intake almost nothing is visible yet, so that one
     * would hide the entire run from the worker sent to photograph it.
     */
    try {
      setLots((await fetchCatalogingLots(supabase, auction.id)) as LotRef[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoadingLots(false);
  }, [auction]);

  useEffect(() => {
    void refreshLots();
  }, [refreshLots]);

  // When lots change, bump currentIndex to the first uncataloged one
  // (or keep it at 0 if none match).
  useEffect(() => {
    if (lots.length === 0) {
      setCurrentIndex(0);
      return;
    }
    const firstUncataloged = lots.findIndex((l) => !l.cataloged_at);
    if (firstUncataloged >= 0) setCurrentIndex(firstUncataloged);
  }, [lots]);

  const currentLot = lots[currentIndex] ?? null;

  // Load photos for the current lot.
  const refreshPhotos = useCallback(async () => {
    if (!currentLot) {
      setPhotos([]);
      return;
    }
    // We need to join lots -> inventory_items to get the photos. Simpler:
    // fetch lot's inventory_item_id first, then pull photos.
    const { data: lotRow } = await supabase
      .from('lots')
      .select('inventory_item_id')
      .eq('id', currentLot.id)
      .maybeSingle();
    if (!lotRow?.inventory_item_id) {
      setPhotos([]);
      return;
    }
    const { data } = await supabase
      .from('inventory_photos')
      .select('id, display_url, image_sequence, display_filename')
      .eq('inventory_item_id', lotRow.inventory_item_id)
      .order('image_sequence', { ascending: true, nullsFirst: false });
    setPhotos((data ?? []) as PhotoRef[]);
  }, [currentLot]);

  useEffect(() => {
    void refreshPhotos();
  }, [refreshPhotos]);

  // Module #28 chunk 4 — offline photo queue.
  // Auto-flush installs once on mount; queue size feeds the "X queued" pill.
  const [queuedCount, setQueuedCount] = useState(0);
  useEffect(() => {
    const tearDownAutoFlush = installAutoFlush({
      onItemUploaded: () => {
        // After a queued photo lands, refresh so the lot indicator updates.
        void Promise.all([refreshPhotos(), refreshLots()]);
      },
    });
    const unsubscribe = subscribeQueueSize(setQueuedCount);
    return () => {
      unsubscribe();
      tearDownAutoFlush();
    };
  }, [refreshPhotos, refreshLots]);

  // Common upload-or-enqueue helper used by both the web file input AND
  // the native camera button. Compresses → tries fetch → enqueues on
  // network failure so the worker swipe never blocks.
  async function uploadOrEnqueue(raw: File): Promise<void> {
    if (!currentLot) return;
    setError(null);
    setUploading(true);
    try {
      const file = await compressImage(raw);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('lot_id', currentLot.id);

      // The photo belongs to whoever is signed in now; if it has to wait in
      // the queue, it goes out later only under their name.
      const ownerId = (await supabase.auth.getSession()).data.session?.user.id ?? null;
      if (ownerId === null) {
        setError(new SignedOutError().message);
        return;
      }

      let res: Response;
      try {
        // The person's token as it is now, with the shared one-refresh retry
        // on a 401 (move P14) — the rule lives in @auctisync/api-client.
        res = await fetchEdgeFunction(
          supabase,
          'upload-photo-to-r2',
          { method: 'POST', body: fd },
          { onlyFor: ownerId },
        );
      } catch (netErr) {
        if (netErr instanceof SignedOutError) {
          // Signed out in the meantime: not a network problem, nothing to queue under.
          setError(netErr.message);
          return;
        }
        // Offline / DNS fail / cors blocked — enqueue for later flush. The
        // capture loop continues; the badge shows pending count.
        await enqueuePhoto({
          file,
          filename: file.name,
          lotId: currentLot.id,
          ownerId,
        });
        setError(
          netErr instanceof Error
            ? `Photo queued (offline): ${netErr.message}`
            : 'Photo queued — will upload when back online.',
        );
        return;
      }

      const raw2 = await res.text();
      let data: { photo?: unknown; error?: string; detail?: string } = {};
      try {
        data = JSON.parse(raw2) as typeof data;
      } catch {
        data = { error: raw2.slice(0, 300) };
      }
      if (!res.ok || data.error) {
        // Server error (not network) — surface but DON'T enqueue, the
        // request reached the server and was rejected. Re-firing won't
        // succeed without intervention.
        setError([`HTTP ${res.status}`, data.error, data.detail].filter(Boolean).join(' · '));
      } else {
        // On first photo the Edge Function stamps cataloged_at — refresh
        // lots so the swipe indicator + needs-research queue updates.
        await Promise.all([refreshPhotos(), refreshLots()]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  // Web file-input flow (works in browser AND in Capacitor WebView, where
  // <input capture> opens the OS camera). The native-camera button below
  // is preferred when running in Capacitor — tighter loop, no Done dialog.
  async function handleCapture(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;
    await uploadOrEnqueue(raw);
  }

  // Capacitor native-camera flow — uses @capacitor/camera plugin for a
  // stable sustained-capture experience under iOS WKWebView (web Camera
  // API has known camera-stops-after-N-photos issues).
  async function handleNativeCapture(): Promise<void> {
    try {
      const file = await capturePhotoNative({ source: 'CAMERA' });
      if (!file) return;
      await uploadOrEnqueue(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // Manual queue retry — surfaced as a small button next to the queue
  // badge. Useful when the worker switches networks (warehouse wifi →
  // cellular) without a navigator 'online' event firing.
  async function handleManualFlush(): Promise<void> {
    setError(null);
    const result = await flushQueue({
      onItemUploaded: () => void Promise.all([refreshPhotos(), refreshLots()]),
    });
    if (result.remaining > 0) {
      setError(`Still ${result.remaining} photo(s) queued — will retry automatically.`);
    }
  }

  async function deletePhoto(photoId: string, opts: { confirm?: boolean } = {}) {
    if (opts.confirm && !window.confirm('Delete this photo?')) return;
    setDeletingId(photoId);
    setError(null);
    try {
      const { data, error: fnErr } = (await supabase.functions.invoke('delete-inventory-photo', {
        body: { photo_id: photoId },
      })) as {
        data: { deleted?: boolean; error?: string } | null;
        error: { message?: string } | null;
      };
      if (fnErr || data?.error || !data?.deleted) {
        setError(data?.error ?? fnErr?.message ?? 'Could not delete photo.');
      } else {
        await refreshPhotos();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  function pickAuction(a: AuctionRef) {
    setAuction(a);
    setSearchParams({ auction: a.id });
    setLots([]);
    setPhotos([]);
    setCurrentIndex(0);
  }

  function exitAuction() {
    setAuction(null);
    setSearchParams({});
  }

  if (!session) {
    return (
      <div style={pageStyle}>
        <p>You must sign in first.</p>
        <Link to="/admin">Sign in</Link>
      </div>
    );
  }

  // Auction picker — mobile-friendly list.
  if (!auction) {
    return (
      <div style={pageStyle}>
        <header style={headerStyle}>
          <div>
            <h1 style={pageTitle}>📸 Cataloging</h1>
            <p style={subtitle}>Pick an auction to photograph.</p>
          </div>
          <Link to="/admin/dashboard" style={exitLink}>
            Exit
          </Link>
        </header>
        {error && <div style={errBox}>{error}</div>}
        {auctions.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', padding: '2rem 1rem', textAlign: 'center' }}>
            No auctions available. Create one in{' '}
            <Link to="/admin/auctions" style={{ color: 'var(--brand-primary)' }}>
              Admin → Auctions
            </Link>
            .
          </p>
        ) : (
          <ul style={listStyle}>
            {auctions.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => pickAuction(a)} style={listButton}>
                  <div style={{ fontWeight: 600, fontSize: '1rem' }}>{a.title}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {a.photo_file_prefix ? (
                      <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                        {a.photo_file_prefix}
                      </code>
                    ) : (
                      <span style={{ fontStyle: 'italic' }}>no prefix set</span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const cataloguedCount = lots.filter((l) => l.cataloged_at).length;

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div>
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {auction.photo_file_prefix ?? 'no prefix'}
          </div>
          <h1 style={{ ...pageTitle, fontSize: '1rem', fontWeight: 600 }}>{auction.title}</h1>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {cataloguedCount} / {lots.length} cataloged
          </div>
        </div>
        <button type="button" onClick={exitAuction} style={exitLink}>
          Exit
        </button>
      </header>

      {loadingLots && (
        <p style={{ color: 'var(--text-muted)', padding: '1rem', textAlign: 'center' }}>
          Loading lots…
        </p>
      )}

      {!loadingLots && lots.length === 0 && (
        <div style={emptyBox}>
          <p style={{ margin: 0 }}>No lots yet on this auction.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Head to the auction page and click <strong>+ Create empty lots</strong> first.
          </p>
          <Link
            to={`/admin/auctions/${auction.id}`}
            style={{ color: 'var(--brand-primary)', marginTop: '0.5rem' }}
          >
            Open auction
          </Link>
        </div>
      )}

      {currentLot && (
        <>
          <div style={lotCardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.6rem',
              }}
            >
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Lot</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text)' }}>
                  #{currentLot.lot_number}
                </div>
                {currentLot.control_number && (
                  <code
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-mono, monospace)',
                    }}
                  >
                    {currentLot.control_number}
                  </code>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '0.25rem',
                }}
              >
                {currentLot.cataloged_at ? (
                  <Badge variant="success">Cataloged</Badge>
                ) : (
                  <Badge variant="outline">Needs photos</Badge>
                )}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {photos.length} photo{photos.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>

            {photos.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(4.5rem, 1fr))',
                  gap: '0.35rem',
                  marginBottom: '0.75rem',
                }}
              >
                {photos.map((p) => {
                  const isDeleting = deletingId === p.id;
                  return (
                    <div key={p.id} style={{ position: 'relative', opacity: isDeleting ? 0.4 : 1 }}>
                      <img
                        src={thumbUrl(p.display_url)}
                        alt={p.display_filename ?? `Photo ${p.image_sequence ?? ''}`}
                        loading="lazy"
                        style={{
                          width: '100%',
                          aspectRatio: '1 / 1',
                          objectFit: 'cover',
                          borderRadius: '0.3rem',
                          border: '1px solid var(--admin-hairline, var(--border))',
                          display: 'block',
                        }}
                      />
                      {p.image_sequence !== null && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: '0.25rem',
                            left: '0.25rem',
                            background: 'rgba(0,0,0,0.55)',
                            color: 'white',
                            fontSize: '0.65rem',
                            padding: '0.1rem 0.3rem',
                            borderRadius: '0.2rem',
                            fontWeight: 600,
                          }}
                        >
                          {p.image_sequence}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label="Delete photo"
                        disabled={isDeleting}
                        onClick={() => {
                          void deletePhoto(p.id, { confirm: true });
                        }}
                        style={{
                          position: 'absolute',
                          top: '0.2rem',
                          right: '0.2rem',
                          width: '1.5rem',
                          height: '1.5rem',
                          borderRadius: '999px',
                          border: 'none',
                          background: 'rgba(185, 28, 28, 0.92)',
                          color: 'white',
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          lineHeight: 1,
                          cursor: isDeleting ? 'not-allowed' : 'pointer',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                void handleCapture(e);
              }}
              style={{ display: 'none' }}
            />
            <Button
              type="button"
              disabled={uploading}
              onClick={() => {
                // Prefer the native plugin on Capacitor — tighter capture
                // loop + no Done dialog round-trip. Falls back to the
                // hidden file input on web.
                if (isNativeCameraAvailable()) {
                  void handleNativeCapture();
                } else {
                  cameraInputRef.current?.click();
                }
              }}
              style={captureButton}
            >
              {uploading ? '⏳ Uploading…' : '📸 Take photo'}
            </Button>
            {queuedCount > 0 && (
              <button
                type="button"
                onClick={() => void handleManualFlush()}
                style={{
                  ...deleteLastButton,
                  background: '#fef3c7',
                  borderColor: '#fcd34d',
                  color: '#78350f',
                }}
                title="Tap to retry uploading queued photos now"
              >
                ↑ {queuedCount} photo{queuedCount === 1 ? '' : 's'} queued — tap to retry
              </button>
            )}
            {photos.length > 0 && (
              <button
                type="button"
                disabled={uploading || Boolean(deletingId)}
                onClick={() => {
                  const last = photos[photos.length - 1];
                  if (!last) return;
                  void deletePhoto(last.id, { confirm: false });
                }}
                style={deleteLastButton}
              >
                {deletingId ? 'Deleting…' : '🗑 Delete last photo'}
              </button>
            )}
          </div>

          {error && <div style={errBox}>{error}</div>}

          <div style={navBar}>
            <Button
              type="button"
              variant="outline"
              disabled={currentIndex === 0 || uploading}
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              style={navButton}
            >
              ← Prev
            </Button>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {currentIndex + 1} / {lots.length}
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={currentIndex >= lots.length - 1 || uploading}
              onClick={() => setCurrentIndex((i) => Math.min(lots.length - 1, i + 1))}
              style={navButton}
            >
              Next →
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  maxWidth: '32rem',
  margin: '0 auto',
  padding: '1rem',
  minHeight: '100vh',
  background: 'var(--surface)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '1rem',
  gap: '1rem',
};

const pageTitle: React.CSSProperties = {
  fontFamily: 'var(--font-heading-active)',
  fontSize: '1.25rem',
  fontWeight: 700,
  color: 'var(--text)',
  margin: 0,
};

const subtitle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--text-muted)',
  margin: '0.2rem 0 0',
};

const exitLink: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--text-muted)',
  textDecoration: 'none',
  border: '1px solid var(--admin-hairline, var(--border))',
  padding: '0.4rem 0.7rem',
  borderRadius: '0.3rem',
  background: 'transparent',
  cursor: 'pointer',
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: '0.5rem',
};

const listButton: React.CSSProperties = {
  width: '100%',
  textAlign: 'left',
  padding: '0.85rem 1rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: '0.4rem',
  background: 'var(--surface-elev)',
  cursor: 'pointer',
  color: 'var(--text)',
};

const lotCardStyle: React.CSSProperties = {
  padding: '1rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  marginBottom: '1rem',
};

const captureButton: React.CSSProperties = {
  width: '100%',
  padding: '1.1rem',
  fontSize: '1.1rem',
  fontWeight: 600,
};

const deleteLastButton: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: '0.5rem',
  padding: '0.55rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--text-danger, #991b1b)',
  background: 'var(--surface)',
  border: '1px solid var(--border-danger, #fecaca)',
  borderRadius: '0.35rem',
  cursor: 'pointer',
};

const navBar: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0',
};

const navButton: React.CSSProperties = {
  flex: '0 0 auto',
  minWidth: '5.5rem',
};

const emptyBox: React.CSSProperties = {
  padding: '1.5rem 1rem',
  textAlign: 'center',
  border: '1px dashed var(--border)',
  borderRadius: 'var(--r-card, 0.5rem)',
  color: 'var(--text-muted)',
  fontSize: '0.9rem',
  display: 'grid',
  gap: '0.4rem',
  justifyContent: 'center',
};

const errBox: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: '0.3rem',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
  marginBottom: '0.75rem',
};
